import { G } from '@mobily/ts-belt';
import { Divider, InputAdornment, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useMemo, useState } from 'react';
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports -- this file is the wrapper; direct import is intentional
  DateInput as DateInputBase,
  TextInputProps,
  Validator,
} from 'react-admin';

import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { type DateTimeString, tryToDateTimeString } from '@sh4res/bo-common/utils/date';
import { supportedDateTimeRangeValidators } from '@sh4res/bo-common/validators/date';

const MIN_DATE_TIME = '1900-01-01T00:00';
const MAX_DATE_TIME = '9999-12-31T23:59';

const dateTimeLocalRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

const FALLBACK_TIME_ZONE = 'UTC';

const getBrowserTimeZone = (): string => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
};

// Reads local (not UTC) getters, matching the convention of date-fns-tz's toZonedTime: it
// returns a Date whose local getters reflect the wall-clock time in the target zone.
const convertZonedDateToDateTimeLocal = (value: Date) => {
  if (Number.isNaN(value.getTime())) {
    return '';
  }

  const yyyy = value.getFullYear().toString().padStart(4, '0');
  const MM = (value.getMonth() + 1).toString().padStart(2, '0');
  const dd = value.getDate().toString().padStart(2, '0');
  const hh = value.getHours().toString().padStart(2, '0');
  const mm = value.getMinutes().toString().padStart(2, '0');
  const ss = value.getSeconds().toString().padStart(2, '0');

  return `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}`;
};

const resolveToInstant = (value: string | Date): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (dateTimeLocalRegex.test(value)) {
    // Naive wall-clock strings (legacy record values) are treated as UTC.
    return new Date(`${value}Z`);
  }

  return new Date(value);
};

export const formatUtcValueForTimezone = (value: string | Date, timezone: string) => {
  if (G.isNullable(value) || value === '') {
    return '';
  }

  const instant = resolveToInstant(value);

  return convertZonedDateToDateTimeLocal(toZonedTime(instant, timezone));
};

// from user timezone input to UTC ISO string (e.g. 2026-01-15T10:00:00.000Z), null when empty
export const parseInputToUtcIsoString = (
  value: string | Date,
  timezone: string,
): DateTimeString | null => {
  if (typeof value === 'string' && dateTimeLocalRegex.test(value)) {
    return tryToDateTimeString(fromZonedTime(value, timezone));
  }

  return tryToDateTimeString(value);
};

const toValidatorArray = (validate: TextInputProps['validate']): Validator[] => {
  if (Array.isArray(validate)) {
    return validate;
  }

  return G.isNotNullable(validate) ? [validate] : [];
};

/**
 * RA DateInput wrapper storing an instant (`DateTimeString`, UTC ISO string) or `null` in the form.
 * The value is displayed and entered in a selectable time zone.
 */
export const DateTimeInput = ({ validate, disabled, ...props }: TextInputProps) => {
  const dataTestId = (props as { 'data-testid'?: string })['data-testid'];
  const validators = [...supportedDateTimeRangeValidators, ...toValidatorArray(validate)];
  const [timezone, setTimezone] = useState<string>(getBrowserTimeZone);
  const timezoneOptions = useMemo(
    () => Array.from(new Set([getBrowserTimeZone(), FALLBACK_TIME_ZONE, 'Europe/Paris'])),
    [],
  );
  const disabledProps = inputDisabledProps(Boolean(disabled));

  return (
    <DateInputBase
      type="datetime-local"
      format={(value: string | Date) => formatUtcValueForTimezone(value, timezone)}
      parse={(value: string | Date) => parseInputToUtcIsoString(value, timezone)}
      validate={validators}
      {...disabledProps}
      inputProps={{ min: MIN_DATE_TIME, max: MAX_DATE_TIME, ...disabledProps.inputProps }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Select
              value={timezone}
              onChange={(event: SelectChangeEvent) => setTimezone(event.target.value)}
              disabled={disabled}
              variant="standard"
              disableUnderline
              aria-label="Time zone"
              data-testid={dataTestId ? `${dataTestId}_TimeZoneSelect` : undefined}
              sx={{ fontSize: '0.875rem' }}>
              {timezoneOptions.map((tz) => (
                <MenuItem
                  key={tz}
                  value={tz}
                  data-testid={dataTestId ? `${dataTestId}_TimeZoneOption` : undefined}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        ),
      }}
      {...props}
    />
  );
};
